/**
 * Product Image Settings - Admin Panel
 * Enable/disable product images feature + upload images
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Upload, Image as ImageIcon, Trash2, Check, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function ProductImageSettings() {
  const [settings, setSettings] = useState({
    enabled: false,
    show_on_kasse: true,
    image_size: 'medium',
    show_category: 'all'
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE}/api/product-images/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (error) {
      console.error('Load settings error:', error);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE}/api/product-images/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });
      
      if (response.ok) {
        toast.success('Einstellungen gespeichert!');
      }
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="bg-[#111C32] border-slate-700/50">
      <CardHeader>
        <CardTitle className="flex items-center gap-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          <ImageIcon className="w-5 h-5 text-teal-400" />
          Produktbilder (Optional)
        </CardTitle>
        <p className="text-sm text-slate-400 mt-2">
          Zeige Bilder auf Produkt-Buttons für bessere visuelle Erkennung
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        
        {/* Enable/Disable */}
        <div className="flex items-center justify-between p-4 bg-slate-800/30 rounded-lg border border-slate-700/50">
          <div>
            <Label className="text-white font-medium">Produktbilder aktivieren</Label>
            <p className="text-xs text-slate-400 mt-1">
              Zeigt Bilder auf Kassen-Buttons (empfohlen für Burger-Restaurants)
            </p>
          </div>
          <Switch
            checked={settings.enabled}
            onCheckedChange={(checked) => setSettings(s => ({ ...s, enabled: checked }))}
            className="data-[state=checked]:bg-teal-500"
          />
        </div>

        {settings.enabled && (
          <>
            {/* Image Size */}
            <div>
              <Label className="text-slate-300 mb-3 block">Bildgröße</Label>
              <div className="grid grid-cols-3 gap-3">
                {['small', 'medium', 'large'].map((size) => (
                  <button
                    key={size}
                    onClick={() => setSettings(s => ({ ...s, image_size: size }))}
                    className={`p-4 rounded-lg border-2 transition-all
                              ${settings.image_size === size
                                ? 'border-teal-500 bg-teal-500/15'
                                : 'border-slate-700 bg-slate-800/30 hover:border-slate-600'
                              }`}
                  >
                    <div className="font-medium text-white capitalize">{size}</div>
                    <div className="text-xs text-slate-400 mt-1">
                      {size === 'small' && '80px'}
                      {size === 'medium' && '120px'}
                      {size === 'large' && '160px'}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Which Categories */}
            <div>
              <Label className="text-slate-300 mb-3 block">Für welche Kategorien?</Label>
              <div className="space-y-2">
                {[
                  { value: 'all', label: 'Alle Kategorien' },
                  { value: 'burger_only', label: 'Nur Burger' },
                  { value: 'main_dishes', label: 'Nur Hauptgerichte (Burger, Pizza, Pasta)' }
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setSettings(s => ({ ...s, show_category: option.value }))}
                    className={`w-full p-3 rounded-lg border-2 text-left transition-all
                              ${settings.show_category === option.value
                                ? 'border-teal-500 bg-teal-500/15'
                                : 'border-slate-700 bg-slate-800/30 hover:border-slate-600'
                              }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-white">{option.label}</span>
                      {settings.show_category === option.value && (
                        <Check className="w-4 h-4 text-teal-400" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Info Box */}
            <div className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/30">
              <div className="flex gap-2">
                <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-slate-300">
                  <strong className="text-blue-400">Tipp:</strong> Ohne eigene Fotos zeigen wir automatisch 
                  schöne Gradient-Platzhalter mit passenden Emojis (🍔, 🍕, etc.)
                </div>
              </div>
            </div>
          </>
        )}

        {/* Save Button */}
        <Button
          onClick={saveSettings}
          disabled={saving}
          className="w-full bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-400 hover:to-emerald-400 text-white h-12"
        >
          {saving ? 'Speichern...' : 'Einstellungen speichern'}
        </Button>
      </CardContent>
    </Card>
  );
}
